import * as trace from 'owa-trace';
import isPerformanceResourceSupported from './IsPerformanceResourceSupported';
import safePerformanceMark from './safePerformanceMark';
import { hasQueryStringParameter } from 'owa-querystring';
import { safePerformanceMeasure } from './safePerformanceMeasure';

/**
 * Prerequisites:
 * - This function expects a Performance Marker with the format "${measureName}_s" to exists (use the safePerformanceMarkStart to get this).
 * - If the start mark ("${measureName}_s") is not defined then undefined is returned.
 * - If there are multiple start marks in performance entries with same name then measure uses the most recently added start entry.
 *
 * Working:
 * - This function logs the end mark and returns the measure from start.
 *
 * @param measureName - measureName should be the same as the one used to mark the start measure, this name will be used in logging the measure in performance entries.
 * @param clearMarkers - defaults to true, this tells if we want to clear the start and end markers from performance entries. Send false here to see the markers in PAS results.
 */
export default function safePerformanceMarkEnd(
    measureName: string,
    clearMarkers: boolean = true
): PerformanceEntry {
    // Preliminary checks
    if (!isPerformanceResourceSupported || !measureName || !isStartMarkerPresent(measureName)) {
        return undefined;
    }

    // Mark end
    safePerformanceMark(`${measureName}_e`);

    // Measure
    const measure = safePerformanceMeasure({
        measureName,
        start: `${measureName}_s`,
        end: `${measureName}_e`,
        clearMarkers: clearMarkers && !hasQueryStringParameter('cpm'),
    });

    // Return measure
    return measure;
}

/**
 * Returns if a start marker corresponding to the provided measureName is present or not
 * @param measureName
 */
function isStartMarkerPresent(measureName: string): boolean {
    try {
        const startMeasureName = `${measureName}_s`;
        const results = window.performance.getEntriesByName(startMeasureName);

        const startMarkerPresent = results?.length > 0;
        return startMarkerPresent;
    } catch (e) {
        trace.errorThatWillCauseAlert(
            `isStartMarkerPresent: Failed to find start marker for ${measureName}`,
            e
        );
    }

    return false;
}
